import axiosInstance from "../axiosinstance";

const projectPath = (projectId) =>
  `/api/projects/${encodeURIComponent(projectId)}/look-ahead`;

const draftPath = (projectId, planId) =>
  `${projectPath(projectId)}/drafts/${encodeURIComponent(planId)}`;

export const getLookAheadError = (error) => {
  const payload = error?.response?.data?.error;
  return {
    status: error?.response?.status || 0,
    code: payload?.code || "REQUEST_FAILED",
    message:
      payload?.message ||
      error?.message ||
      "Unable to complete the Look Ahead request.",
    details: payload?.details,
  };
};

export async function getLookAheadDraft(projectId, signal) {
  const response = await axiosInstance.get(`${projectPath(projectId)}/draft`, {
    signal,
  });
  return response.data?.data;
}

export async function createLookAheadDraft(projectId) {
  const response = await axiosInstance.post(`${projectPath(projectId)}/drafts`);
  return {
    plan: response.data?.data,
    created: Boolean(response.data?.created),
  };
}

export async function updateLookAheadAction(
  projectId,
  planId,
  actionId,
  changes,
) {
  const response = await axiosInstance.patch(
    `${draftPath(projectId, planId)}/actions/${encodeURIComponent(actionId)}`,
    changes,
  );
  return response.data?.data;
}

export async function updateLookAheadNotes(projectId, planId, payload) {
  const response = await axiosInstance.patch(
    draftPath(projectId, planId),
    payload,
  );
  return response.data?.data;
}

export async function validateLookAheadDraft(projectId, planId, version) {
  const response = await axiosInstance.post(
    `${draftPath(projectId, planId)}/validate`,
    { version },
  );
  return response.data;
}

export async function regenerateLookAheadDraft(projectId, planId, version) {
  const response = await axiosInstance.post(
    `${draftPath(projectId, planId)}/regenerate`,
    { version },
  );
  return response.data?.data;
}

export async function publishLookAheadDraft(projectId, planId, version) {
  const response = await axiosInstance.post(
    `${draftPath(projectId, planId)}/publish`,
    { version },
  );
  return response.data?.data;
}

export async function getCurrentLookAheadPlan(projectId, signal) {
  const response = await axiosInstance.get(`${projectPath(projectId)}/current`, {
    signal,
  });
  return response.data?.data;
}

export async function getLookAheadHistory(
  projectId,
  { page = 1, pageSize = 20 } = {},
  signal,
) {
  const response = await axiosInstance.get(`${projectPath(projectId)}/plans`, {
    params: { page, page_size: pageSize },
    signal,
  });
  return {
    plans: response.data?.data || [],
    pagination: response.data?.pagination || {
      page,
      page_size: pageSize,
      total: 0,
    },
  };
}

export async function getLookAheadPlan(projectId, planId, signal) {
  const response = await axiosInstance.get(
    `${projectPath(projectId)}/plans/${encodeURIComponent(planId)}`,
    { signal },
  );
  return response.data?.data;
}

export async function downloadLookAheadPlanPdf(projectId, planId, box = "all") {
  const response = await axiosInstance.get(
    `${projectPath(projectId)}/plans/${encodeURIComponent(planId)}/print.pdf`,
    { responseType: "blob", params: { box } },
  );
  const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url; link.download = `JECON-${planId}-${box === "all" ? "All-Shifts" : `Box-${box}`}.pdf`; link.click();
  URL.revokeObjectURL(url);
}

export async function downloadLookAheadPlan(projectId, planId, format = "pdf", box = "all") {
  const mimeTypes = {
    pdf: "application/pdf",
    excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  const extensions = { pdf: "pdf", excel: "xlsx", xlsx: "xlsx" };
  const response = await axiosInstance.get(
    `${projectPath(projectId)}/plans/${encodeURIComponent(planId)}/print`,
    { responseType: "blob", params: { format, box } },
  );
  const ext = extensions[format] || "pdf";
  const url = URL.createObjectURL(new Blob([response.data], { type: mimeTypes[format] }));
  const link = document.createElement("a");
  link.href = url; link.download = `JECON-${planId}-${box === "all" ? "All-Shifts" : `Box-${box}`}.${ext}`; link.click();
  URL.revokeObjectURL(url);
}
